import * as SimPartData from '@ebiz/aditools-simulation/src/part-data/sim-part-data';
import * as AditoolsSpiceParts from '@ebiz/aditools-spice/data/opamps.json';

const LOG_SKIPS = false;
const LOG_SYNTH = false;
const LOG_PARTS = false;
const ARBITRARILY_RESTRICTED_PARTS = [
  'ADA4891-1',
  'ADA4891-2',
  'ADA4891-3',
  'ADA4891-4',
  'ADA4691-2',
  'ADA4691-4',
  'ADA4692-2',
  'ADA4692-4',
];

export const DataType = {
  t_pulldown: 'Pulldown',
  t_float: 'Floating Point',
  t_integer: 'Integer',
  t_string: 'String',
  t_none: 'N/A',
};
export const CardinalityType = {
  c_single: 'single',
  c_multi: 'multi',
  c_none: 'N/A',
};

class PartIndex {
  constructor() {
    this.data = {};
  }
  getIds() {
    return Object.keys(this.data);
  }
  addPart(id, type, value) {
    if (!(id in this.data)) {
      this.data[id] = {};
    }
    this.data[id][type] = value;
  }
  getMergedPart(id) {
    // merged part will have properties partId, the "numeric" properties, and
    // may also have polesZeroes or special for additional properties.
    let mergedPart = { partId: id };
    let part = this.data[id];
    let partIsLive = false;
    let proptest = /^[s0-9-][0-9]+$/;

    // Walk the properties of the part
    // Override order: pdb overrides static, but config overrides all
    if ('static' in part) {
      for (let propId in part['static']) {
        if (proptest.test(propId)) {
          mergedPart[propId] = part['static'][propId];
        }
      }
      partIsLive = true;
    }
    if ('pst' in part) {
      mergedPart['generic'] = part['pst']['0'].generic ? part['pst']['0'].generic : id.toLowerCase();
      for (let propId in part['pst']) {
        if (proptest.test(propId)) {
          let isok = true;
          if (propId in mergedPart) {
            // Don't overwrite a parameter that might be well-formed with one that isn't
            isok = parameterWellFormed(part['pst'][propId]);
          }
          if (isok) {
            mergedPart[propId] = part['pst'][propId];
          }
        }
      }
      partIsLive = true;
    }
    if ('config' in part) {
      for (let propId in part['config']) {
        if (proptest.test(propId)) {
          let isok = true;
          if (propId in mergedPart) {
            // Don't overwrite a parameter that might be well-formed with one that isn't.
            isok = parameterWellFormed(part['config'][propId]);
          }
          if (isok) {
            mergedPart[propId] = part['config'][propId];
          }
        }
      }
      partIsLive = true;
    }
    if (partIsLive && 'spiceModels' in part) {
      mergedPart['spiceModels'] = part['spiceModels'];
    }
    if (partIsLive && 'polesZeroes' in part) {
      mergedPart['polesZeroes'] = part['polesZeroes'];
    }
    if (partIsLive && 'spice' in part) {
      mergedPart['spice'] = part['spice'];
    }
    if (partIsLive && 'nonPick' in part) {
      mergedPart['nonPick'] = part['nonPick'];
    }
    return mergedPart;
  }
}

let parameterWellFormed = function (parameter) {
  return (
    typeof parameter.value !== 'undefined' &&
    parameter.value !== null &&
    typeof parameter.value.length !== 'undefined' &&
    parameter.value.length !== 0 &&
    parameter.value[0] !== ''
  );
};

let interpretParameter = function (parameter, datatype, datavalue) {
  if (
    typeof parameter.value === 'undefined' ||
    parameter.value === null ||
    typeof parameter.value.length === 'undefined'
  ) {
    return [null, 'INVALID'];
  }
  if (parameter.value.length === 0 || (parameter.value.length === 1 && parameter.value[0] === '')) {
    return [null, 'EMPTY'];
  }
  if (datatype === DataType.t_pulldown && datavalue === CardinalityType.c_multi) {
    return [parameter.value.join('; '), null];
  } else {
    return [parameter.value[0], null];
  }
};

let convertPart = function (mergedPart, parameters, logging, requiresExternalSimulation) {
  let part = { partNumber: mergedPart.partId };
  let missingParms = [];
  let emptyParms = [];
  let invalidParms = [];

  // Fill in all the part's parameters based on the table and keep track of we couldn't use
  Object.values(parameters).forEach((parminfo) => {
    if (parminfo.id !== '0') {
      // Skip the name - we've already got it
      if (parminfo.id in mergedPart) {
        let [value, error] = interpretParameter(mergedPart[parminfo.id], parminfo.data, parminfo.value);
        if (error) {
          if (error === 'INVALID') {
            invalidParms.push(`${parminfo.name}(${parminfo.id})`);
          } else if (error === 'EMPTY' && !parminfo.optional && !('default' in parminfo)) {
            emptyParms.push(`${parminfo.name}(${parminfo.id})`);
          }
        } else {
          part[parminfo.name] = value;
        }
      } else {
        if (!parminfo.optional) {
          missingParms.push(`${parminfo.name}(${parminfo.id})`);
        }
      }
    }
  });

  // A second pass to fill in the defaults for the part's parameters based on other parameters
  Object.values(parameters).forEach((parminfo) => {
    if ('default' in parminfo) {
      if (!(parminfo.name in part)) {
        let value = parminfo.default(part);
        if (value === null && !parminfo.optional) {
          emptyParms.push(`${parminfo.name}(${parminfo.id})`);
        } else {
          part[parminfo.name] = value;
        }
      }
    }
  });

  let isAdequatelySimulatable = requiresExternalSimulation
    ? SimPartData.hasTPCdata(mergedPart.partId, SimPartData.PART_TYPES.OP_AMP) &&
      mergedPart['4455']?.displayValue &&
      mergedPart['4117']?.displayValue &&
      mergedPart['4124']?.displayValue &&
      !ARBITRARILY_RESTRICTED_PARTS.includes(mergedPart.partId)
    : true;

  // Record why we're not delivering a part
  if (missingParms.length > 0 || emptyParms.length > 0 || invalidParms.length > 0 || !isAdequatelySimulatable) {
    if (LOG_SKIPS) {
      let causes = [];
      if (invalidParms.length > 0) {
        causes.push(`invalid parms ${invalidParms.join(', ')}`);
      }
      if (emptyParms.length > 0) {
        causes.push(`empty parms ${emptyParms.join(', ')}`);
      }
      if (missingParms.length > 0) {
        causes.push(`missing parms ${missingParms.join(', ')}`);
      }
      if (!isAdequatelySimulatable) {
        causes.push(`doesn't support simulation`);
      }
      logging.info(`Skipping ${mergedPart.partId} due to ${causes.join('; ')} `);
    }
    return null;
  }

  // Tack on extra information
  if ('generic' in mergedPart) {
    part['generic'] = mergedPart['generic'];
  }
  if ('spiceModels' in mergedPart) {
    part['spiceModels'] = mergedPart['spiceModels'];
  }
  if ('polesZeroes' in mergedPart) {
    part['polesZeroes'] = mergedPart['polesZeroes'];
  } else if (mergedPart.partId.substring(0, 2) === 'LT') {
    let GBW = part['gbw'];
    if (LOG_SYNTH) {
      logging.info('Synthesizing polesZeroes for ' + mergedPart.partId);
    }
    part['polesZeroes'] = {
      gbw: GBW,
      domPole: 0.15 * GBW,
      p: [2.5 * GBW],
      z: [0.5 * GBW],
      rhpz: 1e14,
    };
  }
  if ('spice' in mergedPart) {
    part['spice'] = mergedPart['spice'];
  }
  if ('nonPick' in mergedPart) {
    part['nonPick'] = mergedPart['nonPick'];
  }
  return part;
};

let interpretOpAmpIndex = function (index, parameters, logging, requiresExternalSimulation) {
  try {
    let opamps = [];
    index.getIds().forEach((partId) => {
      let convertedPart = convertPart(index.getMergedPart(partId), parameters, logging, requiresExternalSimulation);
      if (convertedPart) {
        opamps.push(convertedPart);
      }
    });
    return opamps;
  } catch (e) {
    logging.warn('Failure occurred interpreting opamp data:', e.message);
    return null;
  }
};

let collectConfigData = function (adiLocale, staticParts, index, config) {
  if (config.cdpsource.useStaticPartsData && staticParts) {
    staticParts.data[adiLocale].forEach((part) => {
      index.addPart(part['0'].displayValue, 'static', part);
    });
  }
  if (config.cdpsource.useLocalPartsData && config.cdpsource.localParts) {
    config.cdpsource.localParts[adiLocale].forEach((part) => {
      index.addPart(part.partId, 'config', part);
    });
  }
  if (config.polesZeroes) {
    Object.keys(config.polesZeroes).forEach((id) => {
      index.addPart(id, 'polesZeroes', config.polesZeroes[id]);
    });
  }
  if (config.spiceModels) {
    Object.keys(config.spiceModels).forEach((id) => {
      index.addPart(id, 'spiceModels', config.spiceModels[id]);
    });
  }
  // Spice Parts are removed in configuration and we will now use
  // the spice parts in aditools-spice
  const spiceParts = AditoolsSpiceParts;
  if (spiceParts) {
    Object.keys(spiceParts).forEach((id) => {
      index.addPart(id, 'spice', spiceParts[id]);
    });
  }
  if (config.nonPicks) {
    Object.keys(config.nonPicks).forEach((id) => {
      index.addPart(id, 'nonPick', config.nonPicks[id].nonPick);
    });
  }
};

let collectPstData = function (ignoreRX, parts, index) {
  let ignoreRegEx = ignoreRX ? new RegExp(ignoreRX) : null;
  parts.data.forEach((part) => {
    if (!ignoreRegEx || !ignoreRegEx.test(part['0'].displayValue)) {
      index.addPart(part['0'].displayValue, 'pst', part);
    }
  });
};

export function collectMockOpAmps(url, logger) {
  logger.info(`Loading opamps from ${url} in browser`);
  return fetch(url).then((response) => {
    return response.json();
  });
}

export function collectOpAmps(cdp, adiLocale, tool, staticParts, parameters, logging, requiresExternalSimulation) {
  let index = new PartIndex();
  index.pstMapping = cdp.getPstMapping();
  return cdp
    .getToolsConfig(tool)
    .then((config) => {
      if (!config) {
        throw 'No config data';
      }
      logging.time('opampsCfgTime');
      collectConfigData(adiLocale, staticParts, index, config);
      logging.timeEnd('opampsCfgTime');
      return config;
    })
    .then((config) => {
      index.pstView = cdp.getPstIndex(config.cdpsource.viewsource);
      return cdp.getPstData(config.cdpsource.datasource).then(async (parts) => {
        if (!parts) {
          throw 'No PST Parts Data';
        }
        logging.time('opampsPstTime');
        collectPstData(config.cdpsource.ignoreInPDB, parts, index);
        logging.timeEnd('opampsPstTime');
        let pst = parts;
        index.pstData = pst;
        return index;
      });
    })
    .then((index) => {
      logging.time('opampsInterpretTime');
      let opamps = interpretOpAmpIndex(index, parameters, logging, requiresExternalSimulation);
      logging.timeEnd('opampsInterpretTime');
      if (LOG_PARTS) {
        console.log('OpAmps:');
        console.log(opamps.map((item) => item.partNumber));
      }
      opamps.pstData = index.pstData;
      opamps.pstViewPromise = index.pstView;
      opamps.pstMappingPromise = index.pstMapping;
      return opamps;
    })
    .catch((error) => {
      logging.error('Error collecting opamp Data: ', error.message);
      return null;
    });
}
